import { memo } from "react";
import { Settings2, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { COLUMNS } from "@/lib/table-columns";
import type { VisibilityState } from "./types";

interface ColumnVisibilityMenuProps {
  visibleColumns: VisibilityState;
  columnOrder: string[];
  onToggleColumn: (key: string, isVisible: boolean) => void;
  onReorderColumns: (newOrder: string[]) => void;
}

interface SortableItemProps {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

const SortableItem = ({
  id,
  label,
  checked,
  onCheckedChange,
}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground active:cursor-grabbing hover:text-foreground"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <Checkbox
        id={`col-${id}`}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="cursor-pointer"
      />
      <label
        htmlFor={`col-${id}`}
        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1 select-none"
      >
        {label}
      </label>
    </div>
  );
};

export const CustomizeColumns = memo(function CustomizeColumns({
  visibleColumns,
  columnOrder,
  onToggleColumn,
  onReorderColumns,
}: ColumnVisibilityMenuProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id as string);
      const newIndex = columnOrder.indexOf(over.id as string);
      onReorderColumns(arrayMove(columnOrder, oldIndex, newIndex));
    }
  };

  // Map order to actual column objects to get labels
  // We only show columns that exist in COLUMNS definition
  const orderedColumns = columnOrder
    .map((key) => COLUMNS.find((c) => c.key === key))
    .filter((c) => c !== undefined && c.key !== "domain") as typeof COLUMNS;

  const draggableColumnIds = orderedColumns.map((c) => c.key);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="ml-auto cursor-pointer">
          <Settings2 className="mr-2 h-4 w-4" />
          Customize Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[320px] p-0">
        <div className="p-4 pb-2 border-b">
          <h4 className="font-medium leading-none">Customize Columns</h4>
          <p className="text-sm text-muted-foreground pt-1.5">
            Drag to reorder, check to show/hide.
          </p>
        </div>
        <div className="max-h-[400px] overflow-y-auto p-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={draggableColumnIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1">
                {orderedColumns.map((col) => (
                  <SortableItem
                    key={col.key}
                    id={col.key}
                    label={col.label}
                    checked={visibleColumns[col.key] !== false}
                    onCheckedChange={(checked) =>
                      onToggleColumn(col.key, checked)
                    }
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </PopoverContent>
    </Popover>
  );
});
